import type { PropsWithChildren, ReactNode } from "react";
import { AlertCircle, ArrowRight, Inbox, LoaderCircle } from "lucide-react";

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function Card({
  children,
  className = "",
}: PropsWithChildren<{ className?: string }>) {
  return <section className={`card ${className}`.trim()}>{children}</section>;
}

export function Badge({
  children,
  tone = "neutral",
}: PropsWithChildren<{
  tone?: "neutral" | "blue" | "green" | "amber" | "red" | "purple";
}>) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className={`switch ${disabled ? "switch--disabled" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        disabled={disabled}
      />
      <span className="switch__track" aria-hidden="true">
        <span className="switch__thumb" />
      </span>
      <span>{label}</span>
    </label>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-state__icon" aria-hidden="true">
        <Inbox size={22} />
      </span>
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

export function InlineMessage({
  tone,
  title,
  children,
}: PropsWithChildren<{ tone: "info" | "warning" | "danger" | "success"; title: string }>) {
  return (
    <div className={`inline-message inline-message--${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <AlertCircle size={18} aria-hidden="true" />
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export function FullPageState({ title, body }: { title: string; body: string }) {
  return (
    <main className="full-page-state">
      <div className="brand-mark" aria-hidden="true">A</div>
      <LoaderCircle className="spin" size={24} />
      <h1>{title}</h1>
      <p>{body}</p>
    </main>
  );
}

export function SectionHeading({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-heading">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function TextButton({ children, onClick }: PropsWithChildren<{ onClick?: () => void }>) {
  return (
    <button className="text-button" type="button" onClick={onClick}>
      {children}
      <ArrowRight size={15} aria-hidden="true" />
    </button>
  );
}

export function formatRelativeTime(value: string): string {
  const deltaMinutes = Math.round((Date.now() - new Date(value).getTime()) / 60_000);
  if (deltaMinutes < 1) return "Just now";
  if (deltaMinutes < 60) return `${deltaMinutes}m ago`;
  const hours = Math.round(deltaMinutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric" }).format(
    new Date(value),
  );
}

export function formatDateTime(value: string, timezone = "Asia/Bangkok"): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}
